"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useMatches } from "@/hooks/useMatches";
import { LIVE_STATUSES } from "@/contants";
import { formatUnixToVNDate, formatHandicapLabel } from "@/utils/format";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { formatCorrectScoresForGrid, splitCorrectScoresListToSections } from "@/utils/matchInfo";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import ICON_ARROW_UP from "@/assets/images/arrow-up.svg";
import viugoTipsAiSparkle from "@/assets/images/viugo/kbv-v16-tips-ai-sparkle.svg";
import OddsCell from "./OddsCell";
import FinanceeOtherBetsDropdown from "@/components/FinanceeOtherBetsDropdown";
import MatchTime from "./MatchTime";
import ViuGoTipsAiModal from "@/components/ViuGoTipsAiModal";

export default function FinanceeMatch({ match }) {
  const { getMatchOdds, getMatchInsight } = useMatches();
  const { getScore, getOdds } = useLiveDataForMatch(match.id);
  const [openOtherOdds, setOpenOtherOdds] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const fontBody = "text-[13px]";
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const carouselRef = useRef(null);
  const [openTipsAiModal, setOpenTipsAiModal] = useState(false);
  const [tipsAiInsightData, setTipsAiInsightData] = useState(null);
  const [loadingTipsAiInsight, setLoadingTipsAiInsight] = useState(false);

  const liveScore = getScore();
  const liveOdds = getOdds();
  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const oddsPeriod = useSelector((state) => state.oddsPeriodReducer) || "full";
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const {
    currentHomeScore,
    currentAwayScore,
    currentHomeHalfScore,
    currentAwayHalfScore,
  } = useMatchData(liveScore, match);
  const odds = useMatchOdds(match, filterOdds, companyIdOdds);
  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    overUnderValue,
    overOdds,
    underOdds,
    overTrend,
    underTrend,
    homeEuropeOdds,
    awayEuropeOdds,
    instantDrawOdds,
    homeEuropeTrend,
    awayEuropeTrend,
    drawTrend,
    handicapHalfValue,
    homeHandicapHalfOdds,
    awayHandicapHalfOdds,
    homeHandicapHalfTrend,
    awayHandicapHalfTrend,
    overUnderHalfValue,
    overHalfOdds,
    underHalfOdds,
    overHalfTrend,
    underHalfTrend,
    hasOverUnderHalfData,
  } = odds;

  const statusNum =
    liveScore?.status !== undefined ? Number(liveScore.status) : Number(match.status);
  const isLive = LIVE_STATUSES.includes(statusNum);
  const homeLogo = match?.homeTeam?.logo || match?.homeLogo || "";
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo || "";

  const {
    date: formattedDate,
    thu: formattedThu,
    gio: formattedGio,
  } = formatUnixToVNDate(match?.matchTime);

  const handleOpenTipsAiModal = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setOpenTipsAiModal(true);
    if (tipsAiInsightData || loadingTipsAiInsight || !match?.id) return;

    setLoadingTipsAiInsight(true);
    try {
      const insightResponse = await getMatchInsight(match.id);
      if (insightResponse?.success && insightResponse?.data) {
        setTipsAiInsightData(insightResponse.data);
      }
    } finally {
      setLoadingTipsAiInsight(false);
    }
  };

  const handleOtherOddsClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenOtherOdds((prev) => !prev);
    if (!openOtherOdds && !otherOddsData) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const goToCarouselSlide = useCallback((index) => {
    setCarouselIndex(index);
    const el = carouselRef.current;
    if (el) {
      const w = el.offsetWidth;
      el.scrollTo({ left: w * index, behavior: "smooth" });
    }
  }, []);

  const handleCarouselScroll = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const w = el.offsetWidth;
    const index = Math.round(el.scrollLeft / w);
    setCarouselIndex(Math.min(1, Math.max(0, index)));
  }, []);

  const goalOddEvenOdds = otherOddsData?.goalOddEvenOdds ?? [];
  const correctScoresListFull = otherOddsData?.correctScoresOdds
    ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds)
    : [];
  const correctScoresListHalf = otherOddsData?.correctScoresHalfOdds
    ? formatCorrectScoresForGrid(otherOddsData.correctScoresHalfOdds)
    : [];
  const correctScoresFull = splitCorrectScoresListToSections(correctScoresListFull);
  const correctScoresHalf = splitCorrectScoresListToSections(correctScoresListHalf);
  const totalCornersOdds = Array.isArray(otherOddsData?.totalCornersOdds) ? otherOddsData.totalCornersOdds : [];
  const totalCornersHalfOdds = Array.isArray(otherOddsData?.totalCornersHalfOdds) ? otherOddsData.totalCornersHalfOdds : [];
  const goalOddEvenFirst = goalOddEvenOdds?.[0] ?? {};
  const hasOtherData =
    goalOddEvenOdds?.length > 0 ||
    correctScoresListFull.length > 0 ||
    totalCornersOdds.length > 0 ||
    correctScoresListHalf.length > 0 ||
    totalCornersHalfOdds.length > 0;

  return (
    <div
      className={`kbv-ktm-card group flex flex-wrap w-full shadow-[0_0_2px_rgba(0,0,0,0.16),0_1px_2px_rgba(0,0,0,0.16)] mb-1.5 last:mb-0 last:rounded-b-xl last:overflow-hidden transition-colors ${isLive ? "kbv-ktm-card--live" : "kbv-ktm-card--not-live"}`}
    >
      <div className="kbv-ktm-border flex flex-col w-full border border-r border-b-0 transition-colors">
        <div className="flex flex-col">
          {/* row 1 Desktop: time, odds */}
          <div className={`kbv-ktm-border mb-hidden md:flex items-stretch text-center w-full min-h-[52px] border-b transition-colors`}>
            <MatchTime
              match={match}
              isLive={isLive}
              formattedThu={formattedThu}
              formattedDate={formattedDate}
              formattedGio={formattedGio}
              currentHomeScore={currentHomeScore}
              currentAwayScore={currentAwayScore}
              liveScore={liveScore}
              statusNum={statusNum}
              matchTime={match?.matchTime}
              halfStartTime={match?.halfStartTime}
              fontBody={fontBody}
            />
            <div className={`kbv-ktm-border flex flex-col justify-start items-start ${oddsPeriod === "half1" ? "w-[55%]" : "w-[22%]"} pl-2.5 pb-1 text-left font-bold border-r border-b-0 transition-colors ${fontBody}`}>
              <div className={`kbv-team-name ${currentHomeScore > currentAwayScore ? "kbv-team-name--win" : "kbv-team-name--lose"} cursor-pointer select-none px-2.5 py-[5px]`}>{match.homeName}</div>
              <div className={`kbv-team-name ${currentAwayScore > currentHomeScore ? "kbv-team-name--win" : "kbv-team-name--lose"} cursor-pointer select-none px-2.5 py-[5px]`}>{match.awayName}</div>
            </div>
            {oddsPeriod === "full" && (

              <div className={`kbv-ktm-border flex flex-col items-center justify-center w-[35%] border-r border-b-0 transition-colors`}>
                <div className="flex flex-row w-full h-full py-0 px-0.5">
                  <div className={`kbv-ktm-border flex flex-col gap-[5px] font-bold justify-start w-1/3 p-[5px] border-r border-b-0 transition-colors ${fontBody}`}>
                    <OddsCell label={formatHandicapLabel(handicapValue, true)} value={homeHandicapOdds} trend={homeHandicapTrend} />
                    <OddsCell label="" value={overOdds} trend={overTrend} />
                  </div>
                  <div className={`kbv-ktm-border flex flex-col gap-[5px] font-bold justify-start w-1/3 p-[5px] border-r border-b-0 transition-colors ${fontBody}`}>
                    <OddsCell label={overUnderValue ? `${overUnderValue}` : "2/2.5"} value={awayHandicapOdds} trend={awayHandicapTrend} />
                    <OddsCell label=" U" value={underOdds} trend={underTrend} />
                  </div>
                  <div className={`flex flex-col gap-[5px] font-bold justify-start w-1/3 p-[5px] border-b-0 ${fontBody}`}>
                    <OddsCell label="" value={homeEuropeOdds} trend={homeEuropeTrend} />
                    <OddsCell label="" value={awayEuropeOdds} trend={awayEuropeTrend} />
                    <OddsCell label="" value={instantDrawOdds} trend={drawTrend} />
                  </div>
                </div>
              </div>
            )}
            <div className={`kbv-ktm-border flex flex-col items-center justify-center w-[35%] border-r border-b-0 transition-colors`}>
              <div className="flex flex-row w-full h-full py-0 px-0.5">
                <div className={`kbv-ktm-border flex flex-col gap-[5px] font-bold justify-start w-1/3 p-[5px] border-r border-b-0 transition-colors ${fontBody}`}>
                  <OddsCell label={formatHandicapLabel(handicapHalfValue, true)} value={homeHandicapHalfOdds} trend={homeHandicapHalfTrend} />
                  <OddsCell label={hasOverUnderHalfData ? overUnderHalfValue : "0.5/1"} value={hasOverUnderHalfData ? overHalfOdds : awayHandicapHalfOdds} trend={hasOverUnderHalfData ? overHalfTrend : awayHandicapHalfTrend} />
                </div>
                <div className={`kbv-ktm-border flex flex-col gap-[5px] font-bold justify-start w-1/3 p-[5px] border-r border-b-0 transition-colors ${fontBody}`}>
                  <OddsCell label={hasOverUnderHalfData ? "" : "0/0.5"} value={hasOverUnderHalfData ? underHalfOdds : homeHandicapHalfOdds} trend={hasOverUnderHalfData ? underHalfTrend : homeHandicapHalfTrend} />
                  <OddsCell label=" U" value={underHalfOdds} trend={underHalfTrend} />
                </div>
                <div className={`flex flex-col gap-[5px] font-bold justify-start w-1/3 p-[5px] border-b-0 ${fontBody}`}>
                  <OddsCell label="" value={homeEuropeOdds} trend={homeEuropeTrend} />
                  <OddsCell label="" value={awayEuropeOdds} trend={awayEuropeTrend} />
                  <OddsCell label="" value={instantDrawOdds} trend={drawTrend} />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile */}
          <div className={`grid md:hidden w-full border-b kbv-ktm-border`} style={{ gridTemplateColumns: "38% 1fr", gridTemplateRows: "auto auto" }}>
            {/* Row 1 time, homeName|awayName */}
            <div className={`kbv-ktm-border flex flex-col min-w-0 border-r border-b`}>
              <MatchTime
                match={match}
                isLive={isLive}
                formattedThu={formattedThu}
                formattedDate={formattedDate}
                formattedGio={formattedGio}
                currentHomeScore={currentHomeScore}
                currentAwayScore={currentAwayScore}
                liveScore={liveScore}
                statusNum={statusNum}
                matchTime={match?.matchTime}
                halfStartTime={match?.halfStartTime}
                fontBody={fontBody}
                fullWidth
              />
              <div className={`flex flex-col justify-start items-start pl-2.5 pb-1 pt-0.5 text-left font-bold ${fontBody}`}>
                <div className={`kbv-team-name ${currentHomeScore > currentAwayScore ? "kbv-team-name--win" : "kbv-team-name--lose"} select-none px-0 py-1`}>{match.homeName}</div>
                <div className={`kbv-team-name ${currentAwayScore > currentHomeScore ? "kbv-team-name--win" : "kbv-team-name--lose"} select-none px-0 py-1`}>{match.awayName}</div>
              </div>
            </div>
            {/* Dòng 1 - Cột 2: kèo theo oddsPeriod (Cả trận / Hiệp 1) từ dropdown trang - scroll ngang mobile */}
            <div className={`kbv-ktm-border flex flex-col flex-1 min-w-0 border-b overflow-x-auto`}>
              <div
                ref={carouselRef}
                onScroll={handleCarouselScroll}
                className="kbv-carousel-scroll flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth flex-1 min-h-0"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                <div className="min-w-full flex-shrink-0 snap-start flex flex-col">
                  <div className="flex flex-row w-full flex-shrink-0 px-1 pb-1">
                    <div className={`flex flex-col flex-1 items-center justify-center text-center text-[10px] text-[#E83344] font-bold kbv-ktm-border border-r pr-1`}>
                      <span>{oddsPeriod === "half1" ? "Hiệp 1" : "Cả trận"}</span>
                      <span>Cược Chấp</span>
                    </div>
                    <div className={`flex flex-col flex-1 items-center justify-center text-center text-[10px] text-[#E83344] font-bold kbv-ktm-border border-r px-0.5`}>
                      <span>{oddsPeriod === "half1" ? "Hiệp 1" : "Cả trận"}</span>
                      <span>Tài/Xỉu</span>
                    </div>
                    <div className="flex flex-col flex-1 items-center justify-center text-center text-[10px] text-[#E83344] font-bold pl-1">
                      <span>{oddsPeriod === "half1" ? "Hiệp 1" : "Cả trận"}</span>
                      <span>1X2</span>
                    </div>
                  </div>
                  <div className="flex flex-row w-full">
                    {oddsPeriod === "half1" ? (
                      <>
                        <div className={`flex flex-col gap-[5px] font-bold justify-start flex-1 p-[5px] kbv-ktm-border ${fontBody}`}>
                          <OddsCell label={formatHandicapLabel(handicapHalfValue, true)} value={homeHandicapHalfOdds} trend={homeHandicapHalfTrend} />
                          <OddsCell label={hasOverUnderHalfData ? overUnderHalfValue : "0.5/1"} value={hasOverUnderHalfData ? overHalfOdds : awayHandicapHalfOdds} trend={hasOverUnderHalfData ? overHalfTrend : awayHandicapHalfTrend} />
                        </div>
                        <div className={`flex flex-col gap-[5px] font-bold justify-start flex-1 p-[5px] kbv-ktm-border ${fontBody}`}>
                          <OddsCell label={hasOverUnderHalfData ? "" : "0/0.5"} value={hasOverUnderHalfData ? underHalfOdds : homeHandicapHalfOdds} trend={hasOverUnderHalfData ? underHalfTrend : homeHandicapHalfTrend} />
                          <OddsCell label=" U" value={underHalfOdds} trend={underHalfTrend} />
                        </div>
                        <div className={`flex flex-col gap-[5px] font-bold justify-start flex-1 p-[5px] ${fontBody}`}>
                          <OddsCell label="" value={homeEuropeOdds} trend={homeEuropeTrend} />
                          <OddsCell label="" value={awayEuropeOdds} trend={awayEuropeTrend} />
                          <OddsCell label="" value={instantDrawOdds} trend={drawTrend} />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={`flex flex-col gap-[5px] font-bold justify-start flex-1 p-[5px] kbv-ktm-border ${fontBody}`}>
                          <OddsCell label={formatHandicapLabel(handicapValue, true)} value={homeHandicapOdds} trend={homeHandicapTrend} />
                          <OddsCell label="" value={overOdds} trend={overTrend} />
                        </div>
                        <div className={`flex flex-col gap-[5px] font-bold justify-start flex-1 p-[5px] kbv-ktm-border ${fontBody}`}>
                          <OddsCell label={overUnderValue ? `${overUnderValue}` : "2/2.5"} value={awayHandicapOdds} trend={awayHandicapTrend} />
                          <OddsCell label=" U" value={underOdds} trend={underTrend} />
                        </div>
                        <div className={`flex flex-col gap-[5px] font-bold justify-start flex-1 p-[5px] ${fontBody}`}>
                          <OddsCell label="" value={homeEuropeOdds} trend={homeEuropeTrend} />
                          <OddsCell label="" value={awayEuropeOdds} trend={awayEuropeTrend} />
                          <OddsCell label="" value={instantDrawOdds} trend={drawTrend} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {oddsPeriod === "full" && (
                  <>
                    <div className="min-w-full flex-shrink-0 snap-start flex flex-col">
                      <div className="flex flex-row w-full flex-shrink-0 px-1 pb-1">
                        <div className={`flex flex-col flex-1 items-center justify-center text-center text-[10px] text-[#E83344] font-bold kbv-ktm-border border-r pr-1`}>
                          <span>Hiệp 1</span>
                          <span>Cược Chấp</span>
                        </div>
                        <div className={`flex flex-col flex-1 items-center justify-center text-center text-[10px] text-[#E83344] font-bold kbv-ktm-border border-r px-0.5`}>
                          <span>Hiệp 1</span>
                          <span>Tài/Xỉu</span>
                        </div>
                        <div className="flex flex-col flex-1 items-center justify-center text-center text-[10px] text-[#E83344] font-bold pl-1">
                          <span>Hiệp 1</span>
                          <span>1X2</span>
                        </div>
                      </div>
                      <div className="flex flex-row w-full">
                        <div className={`flex flex-col gap-[5px] font-bold justify-start flex-1 p-[5px] kbv-ktm-border ${fontBody}`}>
                          <OddsCell label={formatHandicapLabel(handicapHalfValue, true)} value={homeHandicapHalfOdds} trend={homeHandicapHalfTrend} />
                          <OddsCell label={hasOverUnderHalfData ? overUnderHalfValue : "0.5/1"} value={hasOverUnderHalfData ? overHalfOdds : awayHandicapHalfOdds} trend={hasOverUnderHalfData ? overHalfTrend : awayHandicapHalfTrend} />
                        </div>
                        <div className={`flex flex-col gap-[5px] font-bold justify-start flex-1 p-[5px] kbv-ktm-border ${fontBody}`}>
                          <OddsCell label={hasOverUnderHalfData ? "" : "0/0.5"} value={hasOverUnderHalfData ? underHalfOdds : homeHandicapHalfOdds} trend={hasOverUnderHalfData ? underHalfTrend : homeHandicapHalfTrend} />
                          <OddsCell label=" U" value={underHalfOdds} trend={underHalfTrend} />
                        </div>
                        <div className={`flex flex-col gap-[5px] font-bold justify-start flex-1 p-[5px] ${fontBody}`}>
                          <OddsCell label="" value={homeEuropeOdds} trend={homeEuropeTrend} />
                          <OddsCell label="" value={awayEuropeOdds} trend={awayEuropeTrend} />
                          <OddsCell label="" value={instantDrawOdds} trend={drawTrend} />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {/* Dấu chấm tròn 5px: Cả Trận / Hiệp 1 */}
              <div className="flex justify-center items-center gap-1.5 py-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => goToCarouselSlide(0)}
                  aria-label="Cả Trận"
                  className={`p-0 border-0 flex-shrink-0 min-w-[5px] min-h-[5px] w-[5px] h-[5px] rounded-full box-border transition-colors ${carouselIndex === 0 ? (isLive ? "kbv-ktm-dot--active-live" : "kbv-ktm-dot--active-not-live") : "kbv-ktm-dot--inactive"}`}
                />
                <button
                  type="button"
                  onClick={() => goToCarouselSlide(1)}
                  aria-label="Hiệp 1"
                  className={`p-0 border-0 flex-shrink-0 min-w-[5px] min-h-[5px] w-[5px] h-[5px] rounded-full box-border transition-colors ${carouselIndex === 1 ? (isLive ? "kbv-ktm-dot--active-live" : "kbv-ktm-dot--active-not-live") : "kbv-ktm-dot--inactive"}`}
                />
              </div>
            </div>
            {/* Row 2: data button (mobile: sát trái) */}
            <div className="flex justify-start items-center py-2 px-1 border-r kbv-ktm-border">
              <button
                type="button"
                onClick={handleOpenTipsAiModal}
                className="md:hidden kbv-tips-btn-indigo-slate relative flex h-8 w-[140px] shrink-0 cursor-pointer items-center justify-center gap-0.5 whitespace-nowrap rounded-[8px] border border-solid px-2 text-[11px] font-medium leading-[15px] text-white"
              >
                <img
                  src={viugoTipsAiSparkle}
                  alt=""
                  width={16}
                  height={16}
                  className="h-3.5 w-3.5 shrink-0"
                  aria-hidden
                />
                <div>Tips AI</div>
              </button>
            </div>
            {/* Row 2 other odds button (mobile: hình vuông, full width) */}
            <div className="flex justify-center items-center py-2 px-1.5">
              <div
                role="button"
                tabIndex={0}
                onClick={handleOtherOddsClick}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleOtherOddsClick(); } }}
                className="betOrder flex justify-center items-center py-1.5 px-1.5 cursor-pointer select-none w-full md:max-w-[200px]"
              >
                <div className={`contentOrder ${isLive ? "contentOrder--live" : "contentOrder--not-live"} ${openOtherOdds ? "active" : ""} flex items-center justify-center gap-2 py-2 px-4 font-bold ${fontBody} bg-[#FDF3E7] text-[var(--theme-text)] rounded-none border-0 w-full transition-colors [clip-path:none] md:[clip-path:polygon(0_0,100%_0,90%_100%,10%_100%)]`}>
                  Kèo khác
                  <img src={ICON_ARROW_UP} alt="" width={16} height={16} className={`shrink-0 transition-transform duration-300 ${openOtherOdds ? "rotate-180" : "rotate-0"}`} />
                </div>
              </div>
            </div>
          </div>

          {/* Row 2 Desktop: data button, other odds button */}
          <div className={`mb-hidden md:flex items-stretch w-full text-center min-h-[52px] border-b kbv-ktm-border`}>
            <div className={`flex justify-start items-center flex-wrap w-[10%] min-w-[34px] border-r kbv-ktm-border border-l-0 border-b-0 transition-colors group-hover:border-r-[var(--theme-foreground)] py-2.5 pl-2.5`}>
              <button
                type="button"
                onClick={handleOpenTipsAiModal}
                className="kbv-tips-btn-indigo-slate relative flex h-[26px] w-max max-w-full shrink-0 cursor-pointer items-center justify-center gap-0.5 whitespace-nowrap rounded-[8px] border border-solid p-[3px] text-[11px] font-medium leading-[15px] text-white max-lg:w-max lg:h-auto lg:gap-1 lg:px-3 lg:py-2 lg:text-[12px]"
              >
                <img
                  src={viugoTipsAiSparkle}
                  alt=""
                  width={16}
                  height={16}
                  className="h-3.5 w-3.5 shrink-0 lg:h-4 lg:w-4"
                  aria-hidden
                />
                <div>Tips AI</div>
              </button>
            </div>
            <div className={`w-[22%] flex items-center justify-center py-2.5 px-0 border-r kbv-ktm-border border-b-0 transition-colors group-hover:border-r-[var(--theme-foreground)]`} />
            <div className={`flex items-stretch w-[70%] border-r kbv-ktm-border border-b-0 transition-colors group-hover:border-r-[var(--theme-foreground)]`}>
              <div className={`flex justify-center items-center flex-[5] min-w-0 py-2.5 px-1.5 border-r kbv-ktm-border transition-colors group-hover:border-r-[var(--theme-foreground)]`}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={handleOtherOddsClick}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleOtherOddsClick(); } }}
                  className="betOrder flex justify-center items-center py-1.5 px-1.5 cursor-pointer select-none w-[250px] max-w-full"
                >
                  <div className={`contentOrder ${isLive ? "contentOrder--live" : "contentOrder--not-live"} ${openOtherOdds ? "active" : ""} flex items-center justify-center gap-2 py-2 px-5 font-bold ${fontBody} bg-[#FDF3E7] text-[var(--theme-text)] rounded-none border-0 w-full max-w-[250px] transition-colors [clip-path:polygon(0_0,100%_0,90%_100%,10%_100%)]`}>
                    Kèo khác
                    <img src={ICON_ARROW_UP} alt="" width={16} height={16} className={`shrink-0 transition-transform duration-300 ${openOtherOdds ? "rotate-180" : "rotate-0"}`} />
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {openOtherOdds && (
          <FinanceeOtherBetsDropdown
            loadingOtherOdds={loadingOtherOdds}
            otherOddsData={otherOddsData}
            hasOtherData={hasOtherData}
            isLive={isLive}
            goalOddEvenOdds={goalOddEvenOdds}
            goalOddEvenFirst={goalOddEvenFirst}
            correctScoresListFull={correctScoresListFull}
            correctScoresFull={correctScoresFull}
            correctScoresListHalf={correctScoresListHalf}
            correctScoresHalf={correctScoresHalf}
            totalCornersOdds={totalCornersOdds}
            totalCornersHalfOdds={totalCornersHalfOdds}
            fontBody={fontBody}
          />
        )}
      </div>

      <ViuGoTipsAiModal
        open={openTipsAiModal}
        onClose={() => setOpenTipsAiModal(false)}
        match={match}
        matchInfo={{
          formattedDate,
          formattedThu,
          formattedGio,
          isLive,
        }}
        statsInfo={{
          currentHomeScore,
          currentAwayScore,
          currentHomeHalfScore,
          currentAwayHalfScore,
          homeYellow: null,
          homeRed: null,
          awayYellow: null,
          awayRed: null,
          currentHomeCorner: null,
          currentAwayCorner: null,
        }}
        oddsInfo={{
          handicapValue,
          homeHandicapOdds,
          awayHandicapOdds,
          overUnderValue,
          overOdds,
          underOdds,
          homeEuropeOdds,
          awayEuropeOdds,
          instantDrawOdds,
          handicapHalfValue,
          homeHandicapHalfOdds,
          awayHandicapHalfOdds,
        }}
        leagueName={match?.league?.name || match?.leagueName}
        leagueLogo={match?.league?.logo || match?.leagueLogo || ""}
        homeName={match?.homeName}
        homeLogo={homeLogo}
        awayName={match?.awayName}
        awayLogo={awayLogo}
        matchDate={formattedDate}
        matchTime={formattedGio}
        matchTimestamp={match?.matchTime}
        headerScore={`${currentHomeScore ?? 0} - ${currentAwayScore ?? 0}`}
        liveScoreToday={liveScore}
        matchStatusNum={statusNum}
        matchTimeUnix={match?.matchTime}
        halfStartTime={match?.halfStartTime}
        isMatchLive={isLive}
        statusLabel={!isLive ? "Sắp diễn ra" : "Trực tiếp"}
        insightData={tipsAiInsightData}
        insightLoading={loadingTipsAiInsight}
      />
    </div>
  );
}
